import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Quote, MessageSquarePlus, Loader2 } from "lucide-react";
import { FadeUp, RevealLine } from "./AnimatedSection";
import { useLanguage } from "@/context/LanguageContext";
import { useLenisPause } from "@/hooks/useLenisPause";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const InitialAvatar = ({ name, accent }) => {
  const initial = (name || "?").trim().charAt(0).toUpperCase();
  return (
    <div
      className={`w-11 h-11 rounded-full flex items-center justify-center font-heading font-bold text-sm text-white border border-white/10 shrink-0 ${
        accent === "red" ? "bg-brand-red/80" : "bg-brand-blue/80"
      }`}
    >
      {initial}
    </div>
  );
};

const TestimonialCard = ({ item, index }) => {
  const accent = index % 2 === 0 ? "text-brand-red" : "text-brand-blue";
  const ring = index % 2 === 0 ? "hover:border-brand-red/40" : "hover:border-brand-blue/40";
  return (
    <FadeUp
      delay={(index % 4) * 0.08}
      data-testid={`testimonial-card-${index}`}
      className={`rounded-2xl border border-white/10 bg-white/[0.03] p-7 flex flex-col gap-5 transition-all duration-300 hover:-translate-y-1 ${ring}`}
    >
      <Quote className={`w-6 h-6 ${accent}`} strokeWidth={1.5} />
      <p className="text-neutral-300 text-[15px] leading-relaxed flex-1">&ldquo;{item.quote}&rdquo;</p>
      <div className="flex items-center gap-3 pt-2 border-t border-white/[0.06]">
        <InitialAvatar name={item.name} accent={index % 2 === 0 ? "red" : "blue"} />
        <div>
          <p className="text-white font-heading font-bold text-sm">{item.name}</p>
          <p className="text-neutral-500 text-xs">{item.role}</p>
        </div>
      </div>
    </FadeUp>
  );
};

export const Testimonials = () => {
  const { t } = useLanguage();
  const [dynamicItems, setDynamicItems] = useState([]);
  const [open, setOpen] = useState(false);
  useLenisPause(open);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", quote: "" });

  const fetchTestimonials = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/testimonials`);
      setDynamicItems(res.data || []);
    } catch {
      setDynamicItems([]);
    }
  }, []);

  useEffect(() => {
    fetchTestimonials();
  }, [fetchTestimonials]);

  const allItems = [...t.testimonials.items, ...dynamicItems];

  const handleSubmit = async (e) => {
    e.preventDefault();
    const tt = t.testimonials;
    if (form.name.trim().length < 2 || form.role.trim().length < 2 || form.quote.trim().length < 20) {
      toast.error(tt.validationMessage);
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/testimonials`, form);
      setDynamicItems((prev) => [...prev, res.data.testimonial]);
      toast.success(tt.successMessage);
      setForm({ name: "", role: "", quote: "" });
      setOpen(false);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(tt.rateLimitMessage);
      } else {
        toast.error(tt.errorMessage);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="testimonials-section" className="relative py-28 md:py-36 bg-[#0A0A0A] overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-red/[0.08] via-transparent to-transparent animate-float" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-brand-blue/[0.08] via-transparent to-transparent animate-float-slow" />
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
          <div>
            <p className="text-brand-red text-xs font-mono tracking-[0.25em] font-semibold uppercase mb-4">{t.testimonials.label}</p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white tracking-tight max-w-2xl mb-5">
              <RevealLine text={t.testimonials.title} />
            </h2>
            <div data-testid="testimonials-count-badge" className="flex items-center gap-3">
              <div aria-hidden="true" className="flex items-center -space-x-2.5">
                {allItems.slice(0, 4).map((item, i) => (
                  <div key={item.id || item.name} className="border-2 border-[#0A0A0A] rounded-full">
                    <InitialAvatar name={item.name} accent={i % 2 === 0 ? "red" : "blue"} />
                  </div>
                ))}
              </div>
              <span className="text-neutral-400 text-sm font-medium">
                <span className="text-white font-bold">+{allItems.length}</span> {t.testimonials.countLabel}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-start gap-2">
            <p className="text-neutral-500 text-sm">{t.testimonials.ctaText}</p>
            <button
              data-testid="testimonial-open-form-button"
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 h-11 px-5 rounded-full bg-white/5 border border-white/10 text-white text-sm font-semibold hover:bg-white/10 hover:border-brand-red/40 transition-colors"
            >
              <MessageSquarePlus className="w-4 h-4 text-brand-red" />
              {t.testimonials.ctaButton}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <FadeUp data-testid="testimonial-spotlight" className="lg:col-span-2 rounded-3xl border border-white/10 bg-gradient-to-br from-white/[0.05] to-transparent p-8 md:p-12 relative overflow-hidden">
            <Quote className="w-10 h-10 text-brand-red/40 mb-6" strokeWidth={1.5} />
            <p className="text-white text-xl md:text-2xl font-heading font-medium leading-snug max-w-3xl mb-8">&ldquo;{allItems[0].quote}&rdquo;</p>
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center font-heading font-bold text-lg text-white border border-white/10 shrink-0 bg-brand-red/80">
                {(allItems[0].name || "?").trim().charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-white font-heading font-bold text-base">{allItems[0].name}</p>
                <p className="text-neutral-500 text-sm">{allItems[0].role}</p>
              </div>
            </div>
          </FadeUp>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {allItems.slice(1).map((item, i) => (
            <TestimonialCard key={item.id || item.name} item={item} index={i} />
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="testimonial-form-dialog" className="bg-[#0A0A0A] border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-white">{t.testimonials.formTitle}</DialogTitle>
            <DialogDescription className="text-neutral-500">{t.testimonials.formDescription}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div>
              <label className="text-xs text-neutral-400 mb-1.5 block">{t.testimonials.fieldName}</label>
              <Input
                data-testid="testimonial-input-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={t.testimonials.placeholderName}
                className="bg-white/[0.03] border-white/10 text-white"
                maxLength={80}
              />
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1.5 block">{t.testimonials.fieldRole}</label>
              <Input
                data-testid="testimonial-input-role"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                placeholder={t.testimonials.placeholderRole}
                className="bg-white/[0.03] border-white/10 text-white"
                maxLength={80}
              />
            </div>
            <div>
              <label className="text-xs text-neutral-400 mb-1.5 block">{t.testimonials.fieldQuote}</label>
              <Textarea
                data-testid="testimonial-input-quote"
                value={form.quote}
                onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))}
                placeholder={t.testimonials.placeholderQuote}
                className="bg-white/[0.03] border-white/10 text-white min-h-[110px]"
                maxLength={600}
              />
            </div>
            <Button
              type="submit"
              data-testid="testimonial-submit-button"
              disabled={submitting}
              className="mt-1 bg-brand-red hover:bg-brand-red/90 text-white font-semibold rounded-full h-11"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {submitting ? t.testimonials.submitting : t.testimonials.submitButton}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};
